/**
 * Cross-Link Verifier
 *
 * Render-time guard for the cross-page links the blurb generators emit. Blurbs
 * are generated against a fixed catalog of linkable pages (`/floor-plans`,
 * `/amenities`, …) but any given site only ships a subset of those pages. For
 * each internal `<a href="/x">…</a>` this pass does one of three things:
 *   - `/x` is a real page on this site -> keep the anchor unchanged.
 *   - `/x` matches a real page under a different path alias (via the synonym
 *     table, e.g. "/pets" when the site serves "/pet-policy") -> rewrite the
 *     href to the path the site actually serves, so the link isn't dead.
 *   - `/x` resolves to nothing -> unwrap the anchor to its inner text so the
 *     phrase survives but the dead link disappears.
 *
 * This is link-only. It must NOT touch field tokenization — `resolveRichText`
 * owns that and runs before this pass. Run order in rich-text-content.tsx:
 *   resolveRichText(...)  ->  verifyCrossLinks(...)  ->  dangerouslySetInnerHTML
 *
 * External links (`http(s)://`, protocol-relative, etc.) and `tel:` / `mailto:`
 * anchors pass through untouched — only internal, path-style hrefs are checked.
 */

// Matches a full anchor element: opening tag (capturing attrs), inner content, close.
// Inner content is matched non-greedily; nested anchors aren't valid HTML so this
// is sufficient for the generator's flat `<a>phrase</a>` output.
const ANCHOR_EL_RE = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;

// Pulls the href value out of an anchor's attribute string (single or double
// quoted). The leading `(?:^|\s)` prevents matching the `href` tail of another
// attribute name like `data-field-href=` (a plain `\bhref` would, because the
// `-` before `href` is a word boundary), which would otherwise read the wrong
// value or hijack an anchor that has no real `href` at all.
const HREF_RE = /(?:^|\s)href\s*=\s*("([^"]*)"|'([^']*)')/i;

// Trailing query string and/or hash fragment of a path (e.g. "?ref=home#top").
const QUERY_OR_HASH_RE = /[?#].*$/;

/**
 * Synonym map for internal paths: alias -> equivalent path(s). When an anchor's
 * href is a key here and ANY of its equivalents exists in the site's page set,
 * the link is kept. A value may be a single path or a list of alternatives.
 * Bidirectional pairs are listed both ways. Extend as themes introduce new path
 * conventions.
 */
const PATH_SYNONYMS: Record<string, string | string[]> = {
  '/contact': '/contact-us',
  '/contact-us': '/contact',
  '/floorplans': '/floor-plans',
  '/floor-plans': '/floorplans',
  '/tour': '/schedule-a-tour',
  '/schedule-tour': '/schedule-a-tour',
  '/gallery': '/photos',
  '/photos': '/gallery',
  // Pet page: the generator emits "/pet-policy", but sites name this page
  // variously. Bridge the known variants so the link survives regardless of the
  // site's actual path (and so older stored blurbs that still link "/pets" work).
  '/pet-policy': ['/pets', '/petpolicy', '/pet-friendly'],
  '/pets': ['/pet-policy', '/petpolicy', '/pet-friendly'],
  '/petpolicy': ['/pets', '/pet-policy', '/pet-friendly'],
  '/pet-friendly': ['/pets', '/pet-policy', '/petpolicy'],
};

/**
 * Normalize a path for comparison: lower-case, drop query/hash, strip a single
 * trailing slash (but keep the root "/"). Returns null for anything that isn't
 * an internal absolute path (external URLs, tel:, mailto:, anchors, relative).
 */
function normalizeInternalPath(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;

  // Only treat root-absolute paths ("/x") as internal. Everything else —
  // "http(s)://", "//cdn", "tel:", "mailto:", "#frag", "page.html" — is left
  // alone by the caller.
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null; // protocol-relative external

  // Drop query string and hash before comparing.
  const path = trimmed.split(/[?#]/, 1)[0].toLowerCase();
  if (path === '/') return '/';
  return path.replace(/\/+$/, '');
}

/**
 * Build a fast lookup set of normalized available paths.
 */
function buildPathSet(availablePaths: readonly string[]): Set<string> {
  const set = new Set<string>();
  for (const p of availablePaths) {
    const norm = normalizeInternalPath(p);
    if (norm) set.add(norm);
  }
  return set;
}

/**
 * Resolve an internal path to the path this site actually serves, honoring the
 * synonym table. Returns the matching path from the site's page set — which is
 * the href itself on a direct hit, or a different path when matched via a
 * synonym — or null when nothing matches.
 */
function resolveExistingPath(normalizedHref: string, pathSet: Set<string>): string | null {
  if (pathSet.has(normalizedHref)) return normalizedHref;
  const synonyms = PATH_SYNONYMS[normalizedHref];
  if (synonyms) {
    const candidates = Array.isArray(synonyms) ? synonyms : [synonyms];
    for (const candidate of candidates) {
      const norm = normalizeInternalPath(candidate);
      if (norm && pathSet.has(norm)) return norm;
    }
  }
  return null;
}

/**
 * Verify and prune cross-page links in resolved richtext.
 *
 * @param html           Resolved richtext HTML (post field-token resolution).
 * @param availablePaths The site's real page paths (e.g. from ThemeExports.pages
 *                       / the runtime context). When empty or undefined the pass
 *                       FAILS OPEN — the HTML is returned unchanged so links are
 *                       never silently stripped on sites that don't supply a
 *                       path list.
 * @returns The HTML with dead internal anchors unwrapped to their inner text,
 *          and synonym-matched anchors rewritten to the site's actual path.
 */
export function verifyCrossLinks(html: string, availablePaths?: readonly string[]): string {
  if (!html) return html;
  // Fail open: with no knowledge of the site's pages we can't tell a live link
  // from a dead one, so leave the markup exactly as-is.
  if (!availablePaths || availablePaths.length === 0) return html;

  const pathSet = buildPathSet(availablePaths);
  if (pathSet.size === 0) return html;

  return html.replace(ANCHOR_EL_RE, (full, attrs: string, inner: string) => {
    const hrefMatch = attrs.match(HREF_RE);
    // No href at all — leave it untouched (nothing to verify).
    if (!hrefMatch) return full;
    const href = hrefMatch[2] ?? hrefMatch[3] ?? '';

    const normalized = normalizeInternalPath(href);
    // Not an internal path (external / tel: / mailto: / relative / fragment):
    // pass through untouched.
    if (normalized === null) return full;

    const resolved = resolveExistingPath(normalized, pathSet);
    // Dead internal link — drop the wrapper, keep the phrase.
    if (resolved === null) return inner;
    // Already points at a real page on this site — keep the anchor untouched
    // (preserves the original href's casing / query / hash).
    if (resolved === normalized) return full;

    // Matched a real page under a different path alias (e.g. "/pets" when the
    // site serves "/pet-policy"). Rewrite only the href's path to the path the
    // site actually serves, preserving any query/hash and the rest of the
    // anchor. The replace is scoped to the href attribute (via HREF_RE) so a
    // same-valued earlier attribute (e.g. data-url) is never touched.
    const tail = href.match(QUERY_OR_HASH_RE)?.[0] ?? '';
    const newAttrs = attrs.replace(HREF_RE, m => m.replace(href, `${resolved}${tail}`));
    return `<a${newAttrs}>${inner}</a>`;
  });
}

export default verifyCrossLinks;
